import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { c, trackedBlock } from '../theme';
import { Wordmark } from '../components/Brand';
import { AuthScene, ToneDot } from '../components/AuthScene';
import { api, type Meta } from '../api';

const CREAM_72 = 'rgba(245,238,219,0.72)';
const CREAM_55 = 'rgba(245,238,219,0.55)';
const CREAM_25 = 'rgba(245,238,219,0.25)';
const VIOLET_LIGHT = '#A374F0';
const CREAM_22 = 'rgba(245,238,219,0.22)';
const TICK_MS = 1900;
/** The tone walks these as it goes, so the rail never reads as one long block. */
const TONES = [c.red, c.violet, c.cyan, c.magenta];

/**
 * What a report covers, named before anyone has one to read. Served by
 * `/api/meta` so the screen cannot advertise a module the report does not
 * build; this list only stands in until that arrives.
 */
const FALLBACK_MODULES = [
  'ReadyBuy Bottom Line',
  'The Basics',
  'ReadyBuy Offer Window',
  'Agent Intel',
  'Buyer fit',
  'Street Outlook',
  'Suburb Median',
  'Cost To Own',
  'Red Flags',
  'Recent Growth',
  'What Drives Value',
  'New Development',
  'Lifestyle Score',
  'Who Lives Here',
  'Rent & Yield',
  'Sales & Listings',
  'Schools Nearby',
  'Zoning Rules',
  'Crime & Safety',
  'Buyer Competition',
  'Cultural Mix',
  'Suburbs Like This',
];

/** One module at a time, with a rail showing how far through the set it is. */
function ModuleTicker({ modules }: { modules: string[] }) {
  const [at, setAt] = useState(0);
  const still = useReducedMotion();

  useEffect(() => {
    if (still || modules.length < 2) return;
    const timer = setInterval(() => setAt((n) => (n + 1) % modules.length), TICK_MS);
    return () => clearInterval(timer);
  }, [still, modules.length]);

  // The list can arrive shorter than where the ticker had got to.
  const index = Math.min(at, modules.length - 1);
  const tone = TONES[index % TONES.length];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        paddingTop: 26,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 9, fontWeight: 600, color: CREAM_55, ...trackedBlock(0.18) }}>
          EVERY REPORT INCLUDES
        </div>
        <div style={{ fontSize: 9, fontWeight: 700, color: c.red, whiteSpace: 'nowrap', ...trackedBlock(0.18) }}>
          {modules.length} MODULES
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 22 }} aria-live="off">
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: tone,
            boxShadow: `0 0 10px ${tone}99`,
            flex: 'none',
            transition: 'background 300ms ease',
          }}
        />
        <span style={{ fontSize: 15, fontWeight: 600, color: c.cream, whiteSpace: 'nowrap' }}>
          {modules[index]}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 4 }} aria-hidden>
        {modules.map((m, i) => (
          <span
            key={m}
            style={{
              flex: 1,
              height: 2,
              borderRadius: 999,
              background: i === index ? tone : CREAM_22,
              transition: 'background 300ms ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}

/** Honours the system setting, and keeps honouring it if it changes. */
function useReducedMotion(): boolean {
  const [still, setStill] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setStill(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return still;
}

const CREAM_65 = 'rgba(245,238,219,0.65)';
const CREAM_14 = 'rgba(245,238,219,0.14)';

/** The glass chip with a red tick. */
function GlassChip({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        gap: 10,
        padding: '13px 20px',
        borderRadius: 999,
        border: `1px solid ${CREAM_25}`,
        background: 'rgba(31,26,31,0.35)',
      }}
    >
      <svg width="14" height="11" viewBox="0 0 14 11" fill="none" aria-hidden>
        <path d="M1 5.5 5 9.5 13 1.5" stroke={c.red} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{children}</span>
    </div>
  );
}

const Eyebrow = ({ children }: { children: ReactNode }) => (
  <div style={{ fontSize: 10, fontWeight: 600, color: c.red, ...trackedBlock(0.2) }}>{children}</div>
);

const Row = ({ tone, mark, title, body, tag, size = 22 }: { tone: string; mark?: ReactNode; title: string; body?: string; tag?: string; size?: number }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: tag ? `${size}px 1fr auto` : `${size}px 1fr`,
      alignItems: tag ? 'center' : 'start',
      gap: tag ? 12 : 14,
      padding: tag ? '14px 0' : '13px 0',
      borderTop: `1px solid ${CREAM_14}`,
    }}
  >
    <ToneDot tone={tone} size={size}>{mark}</ToneDot>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ fontSize: tag ? 14 : 14, fontWeight: tag ? 500 : 600, color: c.cream }}>{title}</div>
      {body && <div style={{ fontSize: 11, lineHeight: 1.45, color: CREAM_65, textWrap: 'pretty' }}>{body}</div>}
    </div>
    {tag && <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: tone, whiteSpace: 'nowrap' }}>{tag}</div>}
  </div>
);

/** The first screen a signed-out visitor sees: four steps, then Sign Up. */
export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // The same measured figures the search screen shows, so the welcome screen
  // cannot advertise a count the report does not deliver.
  const [meta, setMeta] = useState<Meta | null>(null);

  useEffect(() => {
    let live = true;
    api
      .meta()
      .then((m) => live && setMeta(m))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const modules = meta?.moduleTitles?.length ? meta.moduleTitles : FALLBACK_MODULES;
  const moduleCount = meta?.modules ?? modules.length;
  // The number on the welcome screen is the module count, the same 22 the
  // report lists — not the median of raw provider read-groups, which wanders.
  const sources = moduleCount;
  const minutes = meta?.estimatedMinutes ?? 5;

  const features: [string, string, string][] = [
    ['ReadyBuy Buyer Fit', 'How the property matches what you and your family actually need — a tailored read you won’t get anywhere else.', c.red],
    ['ReadyBuy Daily Life Index', 'Walkability, transit, schools and amenities — our own index from independent open data, not a resold black-box score.', c.violet],
    ['ReadyBuy Offer Window', 'A defensible value position from independent methods — a range you can see the working behind, not a single guess.', c.cyan],
    ['Comparable sales', 'Recent settled sales nearby, time- and area-adjusted, so you can see the evidence yourself.', c.magenta],
    ['Climate & environment', 'Bushfire, flood, cyclone, coastal and heat — graded now and to 2050 from government + NASA data.', c.red],
    ['Negotiation strategy', 'Concrete levers and a walk-away number, so you go in knowing what they know.', c.violet],
    [`${moduleCount} modules in all`, 'Valuation, comparables, market, location, climate, condition, planning, costs, negotiation — and more.', c.cyan],
  ];
  const how: [string, string, string][] = [
    ['Enter the address', 'Any property in Australia — listed, off-market or just curious.', c.red],
    ['We assemble the evidence', `${moduleCount} modules from licensed property data, the ABS census and government data, in about ${minutes} minutes.`, c.violet],
    ['Walk in ready', 'Your position, your leverage and a walk-away number — before you talk to the agent.', c.cyan],
  ];
  const pricing: [string, string, string][] = [
    ['One report, one payment', '$99', c.red],
    ['Every module, no upsell', `${moduleCount} INCLUDED`, VIOLET_LIGHT],
    ['Yours to keep, in your portfolio', 'IN THE APP', c.cyan],
  ];

  const last = step === 4;
  const next = () => (last ? navigate('/signup') : setStep(step + 1));

  return (
    <AuthScene scrim="dark" minHeight={844}>
      <div style={{ minHeight: 844, display: 'flex', flexDirection: 'column', padding: '44px 30px 28px', boxSizing: 'border-box', flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Wordmark />
          {!last && (
            <button
              onClick={() => navigate('/signup')}
              style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: 'rgba(245,238,219,0.7)' }}
            >
              SKIP
              <svg width="12" height="9" viewBox="0 0 14 10" fill="none" aria-hidden>
                <path d="M1 5h11M8.5 1.5 12 5l-3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>

        {step === 1 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 32 }}>
            <h1 style={{ margin: 0, fontSize: 38, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.02em', textWrap: 'pretty', color: c.cream }}>
              Know what the agent knows<span style={{ color: c.red }}>.</span>
            </h1>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: CREAM_72, textWrap: 'pretty' }}>
              A comprehensive, independent property assessment — tailored to you and your family, not just a market stat sheet.{' '}
              <span style={{ color: c.cream, fontWeight: 600 }}>{sources} sources, 1 report</span>, in minutes. Built for the buyer, never the seller.
            </p>
            <GlassChip>Now you can be ready before you buy.</GlassChip>
            <ModuleTicker modules={modules} />
          </div>
        )}

        {step === 2 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 22, minHeight: 0 }}>
            <Eyebrow>SEE WHAT YOU’RE BUYING</Eyebrow>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {features.map(([title, body, tone]) => (
                <Row key={title} tone={tone} title={title} body={body} />
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 28 }}>
            <Eyebrow>HOW IT WORKS</Eyebrow>
            <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.02em', color: c.cream }}>
              Address in<span style={{ color: c.red }}>.</span>
              <br />
              Evidence out<span style={{ color: c.red }}>.</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {how.map(([title, body, tone], i) => (
                <Row key={title} tone={tone} size={26} mark={String(i + 1)} title={title} body={body} />
              ))}
            </div>
            <GlassChip>Built for the buyer, never the seller.</GlassChip>
          </div>
        )}

        {step === 4 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 28 }}>
            <Eyebrow>SIMPLE PRICING</Eyebrow>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 64, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.03em', color: c.cream }}>$99</span>
                <span style={{ fontSize: 15, fontWeight: 500, color: 'rgba(245,238,219,0.7)' }}>per report</span>
              </div>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: CREAM_72, textWrap: 'pretty', maxWidth: 310 }}>
                One payment, no subscription — ever. Your full {moduleCount}-module report, ready in minutes.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {pricing.map(([title, tag, tone]) => (
                <Row key={title} tone={tone} title={title} tag={tag} />
              ))}
            </div>
            <GlassChip>No card needed to sign up.</GlassChip>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22, paddingTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }} aria-label={`Step ${step} of 4`}>
            {[1, 2, 3, 4].map((n) => (
              <span
                key={n}
                style={{ width: n === step ? 24 : 6, height: 6, borderRadius: 999, background: n === step ? c.red : 'rgba(245,238,219,0.35)', transition: 'width .25s ease' }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                aria-label="Back"
                style={{ width: 54, height: 54, flex: 'none', borderRadius: '50%', border: '1px solid rgba(245,238,219,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.cream }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="M8.5 3L4 7l4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <button
              onClick={next}
              style={{
                flex: 1,
                boxSizing: 'border-box',
                padding: '19px 0',
                borderRadius: 999,
                background: c.violet,
                color: c.cream,
                fontSize: 12,
                fontWeight: 600,
                textAlign: 'center',
                textTransform: 'uppercase',
                ...trackedBlock(0.16),
              }}
            >
              {last ? 'get started' : 'next'}
            </button>
          </div>
          <div style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, textTransform: 'uppercase', color: CREAM_55 }}>
            Already have an account?{' '}
            <button onClick={() => navigate('/signin')} style={{ color: VIOLET_LIGHT, fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, textTransform: 'uppercase' }}>
              sign in
            </button>
          </div>
        </div>
      </div>
    </AuthScene>
  );
}
